// Container hook for PaymentForm — holds ALL of PaymentFormContent's state,
// refs, effects, and async handlers (course/product loading, promo codes, bulk
// pricing, lazy payment-intent creation, and the Stripe submit/confirm flow).
// Extracted VERBATIM from PaymentForm.js so the render becomes a thin orchestrator.
// The 5 params are the props the outer <Elements> wrapper passes to the content.
//
// Decomposed across src/components/payment/paymentForm/usePaymentForm/ into:
//   - useCourseProductFetch: the course/product-loading effect (owns courses,
//     loading, bulkPricing, bulkPricingLoading)
//   - usePromoCode: applyPromoCode/removeBulkPricing/removePromoCode
//   - usePaymentIntent (+ useCreatePaymentIntent): payment-intent lifecycle,
//     getTotalPrice/totalPrice, handleProceedToPayment
//   - useStripeSubmit: handleSubmit (the Stripe confirm flow)
//   - pricing.js / helpers.js / createPaymentIntentHelpers.js /
//     stripeSubmitHelpers.js: pure calculation/classification functions
// All cross-cutting refs (coursesRef, apiCallTimestampsRef, etc.) are created
// here and threaded into whichever sub-hooks touch them, exactly as they were
// shared between the original's inline effects/functions.
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { usePaymentApi } from './paymentApiContext';
import logger from '../../../utils/logging/logger';
import { useCourseProductFetch } from './usePaymentForm/useCourseProductFetch';
import { usePromoCode } from './usePaymentForm/usePromoCode';
import { usePaymentIntent } from './usePaymentForm/usePaymentIntent';
import { useStripeSubmit } from './usePaymentForm/useStripeSubmit';

export function usePaymentForm({ onClientSecretChange, clientSecretFromParent, preservedAppliedPromo, setPreservedAppliedPromo, stripeConfig }) {
  const paymentApi = usePaymentApi(); // signed-in api, or the guest adapter for new students
  const { courseId, courseIds, productIds } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const topAnchorRef = useRef(null);

  // Reset once before paint. Delayed anchor scrolling can jump the viewport
  // when course data arrives between the initial render and the timer.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  const scrollToPaymentTop = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (window.matchMedia('(max-width: 768px)').matches) {
      topAnchorRef.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
    }
  }, []);

  // Pre-fetch CSRF token on mount to prevent "CSRF token is invalid or expired" errors
  useEffect(() => {
    const prefetchCSRFToken = async () => {
      try {
        await paymentApi.fetchCSRFToken();
      } catch (error) {
        logger.warn('Failed to pre-fetch CSRF token', { error });
      }
    };
    prefetchCSRFToken();
  }, [paymentApi]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // FIX: Use preserved promo code from parent if available (survives remounts)
  const [appliedPromo, setAppliedPromo] = useState(preservedAppliedPromo || null);

  // Sync appliedPromo when preserved value changes (from parent)
  // Note: We intentionally don't include appliedPromo in dependencies to avoid infinite loops
  // This effect only runs when preservedAppliedPromo changes from parent
  useEffect(() => {
    if (preservedAppliedPromo && preservedAppliedPromo !== appliedPromo) {
      setAppliedPromo(preservedAppliedPromo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preservedAppliedPromo]);

  // Update preserved promo code in parent whenever it changes
  useEffect(() => {
    if (setPreservedAppliedPromo) {
      setPreservedAppliedPromo(appliedPromo);
    }
  }, [appliedPromo, setPreservedAppliedPromo]);
  const [showCardDivider, setShowCardDivider] = useState(false);
  // FIX: Track if we've already attempted initial payment intent creation to prevent loops on remount
  const initialPaymentIntentAttemptedRef = useRef(!!clientSecretFromParent);
  // FIX #8: Store initial course prices for validation
  const initialCoursePricesRef = useRef(new Map()); // courseId -> price
  // Use refs to track previous values and prevent unnecessary re-renders
  const prevBulkPricingRef = useRef(null);
  const prevAppliedPromoRef = useRef(null);
  // FIX: Use refs to access current state values in setTimeout callbacks (avoid stale closures)
  const appliedPromoRef = useRef(appliedPromo);
  const bulkPricingRef = useRef(null);
  const coursesRef = useRef([]);
  const productIdsRef = useRef(productIds); // FIX: Track productIds to ensure it's available in callbacks

  // Keep refs in sync with state
  useEffect(() => {
    appliedPromoRef.current = appliedPromo;
  }, [appliedPromo]);

  useEffect(() => {
    productIdsRef.current = productIds;
  }, [productIds]);
  const lastPaymentIntentAttemptRef = useRef(null); // Track last attempt to prevent rapid retries
  const paymentIntentCreationLockRef = useRef(false); // Mutex to prevent concurrent payment intent creation
  const abortControllerRef = useRef(null); // FIX #13: AbortController for cancelling API calls on unmount
  const isRestoringBulkPricingRef = useRef(false); // Track if we're restoring bulk pricing to prevent discount change detection
  // FIX #31: Rate limiting for API calls - track last call times
  // FIX #9: Use sliding window approach - only keep recent timestamps
  const apiCallTimestampsRef = useRef({
    createPaymentIntent: 0,
    applyPromoCode: 0,
    removePromoCode: 0,
    removeBulkPricing: 0,
    calculateBulkPricing: 0
  });
  const RATE_LIMIT_DELAY = 1000; // Minimum 1 second between same-type API calls
  const RATE_LIMIT_CLEANUP_INTERVAL = 60000; // Clean up old timestamps every 60 seconds

  // FIX #9: Cleanup old rate limiting timestamps periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const timestamps = apiCallTimestampsRef.current;

      // Clear timestamps older than 5 minutes (no longer relevant for rate limiting)
      Object.keys(timestamps).forEach(key => {
        if (timestamps[key] > 0 && (now - timestamps[key]) > 300000) {
          timestamps[key] = 0;
        }
      });
    }, RATE_LIMIT_CLEANUP_INTERVAL);

    return () => clearInterval(cleanupInterval);
  }, []);
  // FIX #1: Use refs to avoid stale closures in setTimeout callbacks
  const processingRef = useRef(false);
  const paymentIntentCreatedRef = useRef(!!clientSecretFromParent);
  // clientSecret state lives in usePaymentIntent, which itself needs
  // courses/bulkPricing from useCourseProductFetch — this ref mirror (synced
  // below once usePaymentIntent returns clientSecret) breaks that cycle for
  // useCourseProductFetch's few clientSecret reads.
  const clientSecretRef = useRef(clientSecretFromParent || null);

  const {
    courses, loading,
    bulkPricing, setBulkPricing, setBulkPricingLoading,
  } = useCourseProductFetch({
    courseId, courseIds, productIds,
    clientSecretRef,
    clientSecretFromParent,
    setError,
    coursesRef,
    bulkPricingRef,
    initialCoursePricesRef,
    abortControllerRef,
    isRestoringBulkPricingRef,
    prevBulkPricingRef,
    apiCallTimestampsRef,
    paymentIntentCreatedRef,
    RATE_LIMIT_DELAY,
  });

  const {
    applyPromoCode, promoCode, promoError, promoInfo, promoLoading,
    removeBulkPricing, removePromoCode, setPromoCode, setPromoError, setPromoInfo,
  } = usePromoCode({
    courses,
    productIds,
    courseId,
    courseIds,
    bulkPricing,
    setBulkPricing,
    setBulkPricingLoading,
    appliedPromo,
    setAppliedPromo,
    prevBulkPricingRef,
    apiCallTimestampsRef,
    abortControllerRef,
    RATE_LIMIT_DELAY,
    scrollToPaymentTop,
  });

  const {
    clientSecret, setClientSecret, paymentIntentCreated, setPaymentIntentCreated,
    showPaymentForm, creatingPaymentIntent, processing, setProcessing,
    createPaymentIntent, handleProceedToPayment, totalPrice,
  } = usePaymentIntent({
    productIds,
    courses,
    coursesRef,
    appliedPromo,
    appliedPromoRef,
    bulkPricing,
    bulkPricingRef,
    productIdsRef,
    clientSecretFromParent,
    onClientSecretChange,
    stripeConfig,
    loading,
    error,
    setError,
    navigate,
    scrollToPaymentTop,
    paymentIntentCreatedRef,
    paymentIntentCreationLockRef,
    initialPaymentIntentAttemptedRef,
    lastPaymentIntentAttemptRef,
    apiCallTimestampsRef,
    processingRef,
    isRestoringBulkPricingRef,
    prevBulkPricingRef,
    prevAppliedPromoRef,
    RATE_LIMIT_DELAY,
  });

  // Keep refs in sync with state
  useEffect(() => {
    processingRef.current = processing;
  }, [processing]);

  useEffect(() => {
    paymentIntentCreatedRef.current = paymentIntentCreated;
  }, [paymentIntentCreated]);

  useEffect(() => {
    clientSecretRef.current = clientSecret;
  }, [clientSecret]);

  // FIX #26: Clear success state on unmount or when courses change
  // FIX #29: Handle browser back button navigation
  useEffect(() => {
    const handlePopState = () => {
      // Clear success state when user navigates back
      setSuccess(false);
      logger.debug('Browser back button detected, clearing success state');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      // Clear success state when component unmounts
      setSuccess(false);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // FIX #26: Also clear success when navigating away (courses change)
  useEffect(() => {
    setSuccess(false);
  }, [courseId, courseIds, productIds]);

  const { handleSubmit } = useStripeSubmit({
    stripe,
    elements,
    processing,
    success,
    clientSecret,
    clientSecretFromParent,
    setError,
    setPromoError,
    courses,
    initialCoursePricesRef,
    createPaymentIntent,
    setProcessing,
    setClientSecret,
    setPaymentIntentCreated,
    setSuccess,
    productIds,
    navigate,
  });

  return {
    appliedPromo,
    applyPromoCode,
    bulkPricing,
    clientSecret,
    courseId,
    courseIds,
    courses,
    creatingPaymentIntent,
    elements,
    error,
    handleProceedToPayment,
    handleSubmit,
    loading,
    navigate,
    paymentIntentCreated,
    processing,
    productIds,
    promoCode,
    promoError,
    promoInfo,
    promoLoading,
    removeBulkPricing,
    removePromoCode,
    setError,
    setProcessing,
    setPromoCode,
    setPromoError,
    setPromoInfo,
    setShowCardDivider,
    setSuccess,
    showCardDivider,
    showPaymentForm,
    stripe,
    success,
    topAnchorRef,
    totalPrice,
  };
}
